/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The shortlist tray: what the visitor has ticked, and the one link to see
 * those offers side by side on /vergelijk. Renders nothing while the list is
 * empty — also at SSR, where it always is.
 *
 * Fixed at the bottom: a centred pill below `lg` (the FloatingCompareCta
 * family — no backdrop-filter there, a blur under a fixed element is
 * re-sampled on every scrolled frame beneath it), a bottom-right card from
 * `lg`. Its link, hint and clear button are 44 px targets below `lg` (40 px
 * in the card). It never scroll-hides: it is selection state, not
 * navigation. It sits above the cookie banner through `--cookie-bar-height`,
 * which the banner publishes while it is on screen, so a first-time visitor
 * who ticks rows before answering the banner can still reach the link. While
 * it is up, the page's scroll padding keeps a focused control clear of it
 * (WCAG 2.4.12), and the "pick one more" hint is announced from a live region
 * that is always mounted (one that mounts with its text says nothing).
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, m } from 'motion/react';
import { ArrowRight, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/cn';
import { fillTemplate } from '../../lib/templateVars.js';
import { formatInteger } from '../../lib/formatters';
import type { ProviderBackState } from '../../lib/providerBackLink';

export interface ShortlistItem {
  slug: string;
  name: string;
}

interface ShortlistTrayProps {
  /** ≤ 3, in tick order. */
  items: ShortlistItem[];
  /** The /vergelijk URL with the visitor's inputs (comparerParams.buildShortlistHref). */
  href: string;
  /** Router state so /vergelijk can offer "terug naar de vergelijking". */
  backState: ProviderBackState;
  onRemove: (slug: string) => void;
  onClear: () => void;
  onCompareClick: () => void;
}

const BOTTOM_CLASS =
  'bottom-[calc(var(--cookie-bar-height,0px)+max(0.75rem,env(safe-area-inset-bottom)))]';

export const ShortlistTray: React.FC<ShortlistTrayProps> = ({
  items,
  href,
  backState,
  onRemove,
  onClear,
  onCompareClick,
}) => {
  const { t, language } = useLanguage();
  const count = items.length;
  const ready = count >= 2;
  const shown = count > 0;
  const trayRef = useRef<HTMLDivElement>(null);

  // While the tray is up, scrolling a focused control into view (Tab onto a
  // row's CTA or Details) stops above it instead of under it: the page's
  // scroll padding reserves the tray's height, its offset from the bottom edge
  // (the cookie bar's when that is up, plus 0.75 rem) and 1 rem to spare.
  // Removed the moment the tray goes; client-only, like the tray itself.
  useEffect(() => {
    if (!shown) return;
    const tray = trayRef.current;
    if (!tray) return;
    const root = document.documentElement;
    const reserve = () => {
      root.style.scrollPaddingBottom = `calc(${tray.offsetHeight}px + var(--cookie-bar-height, 0px) + 1.75rem)`;
    };
    reserve();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reserve);
    observer?.observe(tray);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('scroll-padding-bottom');
    };
  }, [shown]);
  const selectedText = fillTemplate(t('table.tray_selected'), {
    count: formatInteger(count, language),
  });

  const compareLink = ready ? (
    <Link
      to={href}
      state={backState}
      onClick={onCompareClick}
      className="flex items-center gap-2 h-11 lg:h-10 pl-4 pr-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-black transition-all focus-ring shrink-0"
    >
      {t('table.tray_compare')}
      <ArrowRight aria-hidden="true" className="w-4 h-4" />
    </Link>
  ) : (
    <span className="flex items-center h-11 lg:h-10 px-4 rounded-full bg-slate-800 text-fg-muted text-xs font-bold shrink-0">
      {t('table.tray_min_hint')}
    </span>
  );

  const clearButton = (
    <button
      type="button"
      onClick={onClear}
      aria-label={t('table.tray_clear')}
      title={t('table.tray_clear')}
      className="w-11 h-11 lg:w-10 lg:h-10 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-all focus-ring shrink-0"
    >
      <X aria-hidden="true" className="w-4 h-4" />
    </button>
  );

  return (
    <>
      {/* Says the hint when the first offer is ticked: mounted before it has
          anything to say, empty with none or two-plus ticked. */}
      <span className="sr-only" aria-live="polite">
        {count === 1 ? t('table.tray_min_hint') : ''}
      </span>
      <AnimatePresence>
        {shown && (
          <m.div
            ref={trayRef}
            key="shortlist-tray"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2 }}
            role="region"
            aria-label={selectedText}
            className={cn(
              'fixed inset-x-0 z-40 flex justify-center px-3 pointer-events-none lg:inset-x-auto lg:right-6 lg:justify-end lg:px-0',
              BOTTOM_CLASS
            )}
          >
            <div className="pointer-events-auto max-w-full bg-slate-900/95 border border-slate-700/60 shadow-2xl shadow-slate-950/60 rounded-full p-1 pl-3 flex items-center gap-2 lg:rounded-2xl lg:p-3 lg:pl-4 lg:gap-3 lg:backdrop-blur-xl">
              <div className="min-w-0 flex items-center gap-2">
                <span className="text-sm font-black text-white whitespace-nowrap">
                  {selectedText}
                </span>
                <ul
                  className="hidden lg:flex items-center gap-1 min-w-0"
                  aria-label={t('table.col_compare')}
                >
                  {items.map((item) => (
                    <li key={item.slug} className="min-w-0">
                      <button
                        type="button"
                        onClick={() => onRemove(item.slug)}
                        aria-label={fillTemplate(t('table.compare_remove_aria'), {
                          name: item.name,
                        })}
                        title={fillTemplate(t('table.compare_remove_aria'), { name: item.name })}
                        className="group/chip inline-flex items-center gap-1 max-w-[9rem] h-7 pl-2.5 pr-1.5 rounded-full bg-slate-800 border border-slate-700/60 text-xs font-bold text-slate-200 hover:border-red-400/50 hover:text-white transition-colors focus-ring"
                      >
                        <span className="truncate">{item.name}</span>
                        <X
                          aria-hidden="true"
                          className="w-3 h-3 text-slate-400 group-hover/chip:text-red-300"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              {compareLink}
              {clearButton}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
};
